<script>
import { mapActions } from "vuex";
import { useAlert } from "dashboard/composables";
import {
  offsetMinutesFor,
  todayAndNow,
  filterPastDates,
  filterPastTimeSlots,
} from "dashboard/helper/appointmentPastSlots";

export default {
  props: {
    appointmentId: { type: String, required: true },
    service: { type: Object, required: true },
    staff: { type: Object, required: true },
    branch: { type: Object, required: true },
    previousTime: { type: String, required: true },
    previousDate: { type: String, required: true },
    client_time_info: { type: Object, required: true },
    timezone: { type: String, requried: true }
  },
  data() {
    return {
      startTime: this.previousTime,
      fullDate: this.previousDate.slice(0, 7),
      availableDates: null,
      selectedTimezone: "",
      timezones: null,
      selectedDate: this.previousDate,
      availableTimes: null,
    };
  },
  computed: {
    selectedYear() {
      return this.fullDate.split("-")[0];
    },
    selectedMonth() {
      return this.fullDate.split("-")[1];
    },
    endTime() {
      if (!this.startTime) return "";

      const [hours, minutes, seconds] = this.startTime.split(":").map(Number);

      const totalMinutes = this.service.duration * 60;
      const extraHours = Math.floor(totalMinutes / 60);
      const extraMinutes = Math.floor(totalMinutes % 60);

      let endHours = (hours + extraHours) % 24;
      let endMinutes = minutes + extraMinutes;
      let endSeconds = seconds;

      if (endMinutes >= 60) {
        endHours = (endHours + 1) % 24;
        endMinutes -= 60;
      }

      return `${String(endHours).padStart(2, "0")}:${String(endMinutes).padStart(2, "0")}:${String(endSeconds).padStart(2, "0")}`;
    }
  },
  methods: {
    ...mapActions("appointments", [
      "rescheduleAppointment",
      "fetchAvailableDates",
      "fetchAvailableTimes",
      "getTimezones",
    ]),
    // Today's date and the time right now on the SELECTED timezone's wall clock
    // (not the browser's), the same basis the create modal hides past slots on.
    zoneNow() {
      return todayAndNow(offsetMinutesFor(this.timezones, this.selectedTimezone));
    },
    async checkAvailableDates() {
      if (!this.selectedTimezone) return;
      try {
        const dates = await this.fetchAvailableDates({
          client_timezone: this.selectedTimezone,
          branch_id: this.branch.branch_id,
          staff_id: this.staff.staff_id,
          year: this.selectedYear,
          month: this.selectedMonth,
        });
        const { today } = this.zoneNow();
        this.availableDates = filterPastDates(dates.available_dates, today);
        // The form starts on the appointment's current date and time. If that
        // date is already past, don't leave it selected -- otherwise Reschedule
        // could be submitted straight back into the past.
        if (this.selectedDate && this.selectedDate < today) {
          this.selectedDate = "";
          this.startTime = "";
          this.availableTimes = null;
        }
      } catch (err) {
        console.error(err);
        useAlert(err);
      }
    },
    async checkAvailableTimes() {
      if (!this.selectedDate) return;
      try {
        const times = await this.fetchAvailableTimes({
          client_timezone: this.selectedTimezone,
          branch_id: this.branch.branch_id,
          staff_id: this.staff.staff_id,
          service_id: this.service.service_id,
          date: this.selectedDate,
        });
        const { today, nowHm } = this.zoneNow();
        this.availableTimes = filterPastTimeSlots(
          times.time_slots,
          this.selectedDate,
          "schedule_time",
          today,
          nowHm
        );
        // A time chosen earlier (or prefilled from the appointment) that has
        // already passed on today's date must not stay selected.
        if (
          this.selectedDate === today &&
          this.startTime &&
          this.startTime.slice(0, 5) <= nowHm
        ) {
          this.startTime = "";
        }
      } catch (err) {
        console.error(err);
        useAlert(err);
      }
    },
    async handleSubmit() {
      if (!this.selectedDate || !this.startTime) return;
      try {
        await this.rescheduleAppointment({
          appointmentId: this.appointmentId,
          payload: {
            client_timezone: this.selectedTimezone,
            date: this.selectedDate,
            start_time: this.startTime,
            end_time: this.endTime,
          },
        });
        useAlert(this.$t('APPOINTMENTS.RESCHEDULEMODAL.SUCCESS_MESSAGE'));
        this.$emit("refreshDetails");
        this.$emit("closeModal");
      } catch (err) {
        console.error(err);
        useAlert(err);
      }
    },
    formatTimezone(timezone, timestamp) {
      const match = timestamp.match(/([+-]\d{2}:\d{2})$/);
      const offset = match ? match[1] : "+00:00";
      return `${timezone} (UTC${offset})`;
    },
    getDayOfWeek(date) {
      return new Date(date).toLocaleDateString("en-US", { weekday: "long" });
    },
  },
  emits: ["closeModal", "refreshDetails"],
  async mounted() {
    try {
      this.timezones = await this.getTimezones();
      this.selectedTimezone = this.timezone;
      await this.checkAvailableDates();
      await this.checkAvailableTimes();
    } catch (err) {
      console.error(err);
      useAlert(err);
    }
  },
};
</script>

<template>
    <div data-testid="reschedule_modal" class="p-6 border-2 rounded-lg bg-white dark:bg-slate-800 shadow-lg max-w-md w-full mx-auto space-y-5">
        <div>
            <div class="flex items-center gap-2">
              <h1 class="text-lg font-semibold text-gray-900 dark:text-white">{{ service.service_name }}</h1>
              <fluent-icon 
                v-if="service.is_deleted"
                v-tooltip.top="'This service has been deleted'"
                icon="warning" 
                size="18"
                class="text-amber-500 dark:text-amber-400 flex-shrink-0"
              />
            </div>
            <p class="text-sm text-gray-600 dark:text-gray-300">
                {{ $t('APPOINTMENTS.APPOINTMENT_DETAILS.SERVICE') }}: {{ service.duration }} 
                {{ service.duration > 1 ? $t('APPOINTMENTS.RESCHEDULEMODAL.SERVICE_DURATION_MULTIPLE') : $t('APPOINTMENTS.RESCHEDULEMODAL.SERVICE_DURATION_SINGLE') }}
            </p>
        </div>

        <div class="flex flex-col gap-2">
            <!-- Timezone Selection -->
            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">{{ $t('APPOINTMENTS.RESCHEDULEMODAL.TIMEZONE_LABEL') }}</span>
            <multiselect 
              data-testid="reschedule_timezone"
              v-model="selectedTimezone"
              :options="Object.keys(timezones)"
              :custom-label="formatTimezone"
              :placeholder="$t('APPOINTMENTS.RESCHEDULEMODAL.TIMEZONE_PLACEHOLDER')"
              track-by="timezone"
              label="timezone"
              :close-on-select="true"
              class="w-full text-gray-900 dark:text-white dark:bg-slate-700"
            />

            <!-- Month Selection -->
            <label class="block">
                <span class="text-sm font-medium text-gray-700 dark:text-gray-300">{{ $t('APPOINTMENTS.RESCHEDULEMODAL.MONTH_LABEL') }}</span>
                <input
                data-testid="reschedule_month_input"
                type="month"
                v-model="fullDate"
                :disabled="!selectedTimezone"
                class="w-full p-2 border rounded-md bg-white dark:bg-slate-700 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-woot-500 focus:outline-none disabled:cursor-not-allowed"
                @change="checkAvailableDates"
                />
            </label>

            <!-- Date Selection -->
            <label class="block">
                <span class="text-sm font-medium text-gray-700 dark:text-gray-300">{{ $t('APPOINTMENTS.RESCHEDULEMODAL.DATE_LABEL') }}</span>
                <select
                data-testid="reschedule_date_select"
                v-model="selectedDate"
                :disabled="!availableDates"
                class="w-full border rounded-md bg-white dark:bg-slate-700 dark:border-gray-600 text-gray-900 dark:text-white p-2 focus:ring-woot-500 focus:outline-none disabled:cursor-not-allowed"
                @change="checkAvailableTimes"
                >
                <option v-for="date in availableDates" :value="date" :key="date">
                    {{ `${date}, ${getDayOfWeek(date)}` }}
                </option>
                </select>
            </label>

            <!-- Time Selection -->
            <label class="block">
                <span class="text-sm font-medium text-gray-700 dark:text-gray-300">{{ $t('APPOINTMENTS.RESCHEDULEMODAL.TIME_LABEL') }}</span>
                <select
                data-testid="reschedule_time_select"
                v-model="startTime"
                :disabled="!availableTimes"
                class="w-full border rounded-md bg-white dark:bg-slate-700 dark:border-gray-600 text-gray-900 dark:text-white p-2 focus:ring-woot-500 focus:outline-none disabled:cursor-not-allowed"
                >
                <option v-for="time in availableTimes" :value="`${time.schedule_time}:00`" :key="time.schedule_time">
                    {{ time.schedule_time }}
                </option>
                </select>
            </label>
        </div>

        <!-- Action Buttons -->
        <div class="flex justify-end gap-2">
            <woot-button data-testid="reschedule_cancel" variant="smooth" color-scheme="secondary" @click="$emit('closeModal')">
                {{ $t('APPOINTMENTS.RESCHEDULEMODAL.CANCEL_BUTTON') }}
            </woot-button>
            <woot-button data-testid="reschedule_submit" variant="smooth" color-scheme="primary" @click="handleSubmit" :disabled="!startTime">
                {{ $t('APPOINTMENTS.RESCHEDULEMODAL.RESCHEDULE_BUTTON') }}
            </woot-button>
        </div>
    </div>
</template>

